import { filterWebsitePages } from './workspaceLogic';
import { pagePatch, type PageDraft } from './pageEdits';
import SeoAuditPanel from "./SeoAuditPanel";
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  List,
  ListItem,
  Modal,
  ModalDialog,
  Sheet,
  Stack,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  Option,
  Select,
  Textarea,
  Typography,
  Tooltip,
  Switch,
} from '@mui/joy';
import {
  ArrowLeft as ArrowBackIcon,
  Code as CodeIcon,
  Desktop as DesktopIcon,
  Globe as LanguageIcon,
  ArrowSquareOut as OpenInNewIcon,
  DeviceMobile as MobileIcon,
  ArrowsClockwise as RefreshIcon,
  Rocket as RocketIcon,
  FloppyDisk as SaveIcon,
  MagnifyingGlass as SeoIcon,
  DeviceTablet as TabletIcon,
  SquaresFour as SharedIcon,
  Eye as PreviewIcon,
  SquaresFour as ComponentsIcon,
  Sparkle as EditsIcon,
  CheckCircle as SelectedIcon,
  GearSix as SettingsIcon,
  PaintBrush as BrushIcon,
  Plus as PlusIcon,
} from '@phosphor-icons/react';
import api from '../../../lib/api';
import AddPageDialog from './AddPageDialog';
import CodeEditor from '../../../components/common/CodeEditor';
import {
  LEGAL_PAGE_TYPES,
  type WebsitePageCreateResponse,
  type WebsitePageStatusValue,
  type WebsiteRevisionScope,
  type WebsiteRevisionStatus,
} from '../../../types/websiteBuilder';

// ------- Types -------

const statusColors: Record<WebsitePageStatusValue, 'primary' | 'success' | 'warning' | 'danger' | 'neutral'> = {
  pending: 'neutral',
  generating: 'primary',
  generated: 'success',
  approved: 'success',
  failed: 'danger',
};

const REVISION_STATUS_COLORS: Record<WebsiteRevisionStatus, 'neutral' | 'success' | 'danger' | 'warning'> = {
  pending: 'neutral',
  running: 'neutral',
  succeeded: 'success',
  failed: 'danger',
  no_change: 'warning',
};

const REVISION_STATUS_LABELS: Record<WebsiteRevisionStatus, string> = {
  pending: 'Queued',
  running: 'Applying…',
  succeeded: 'Applied',
  failed: 'Failed',
  no_change: 'No change',
};

const REVISION_SCOPE_LABELS: Partial<Record<WebsiteRevisionScope, string>> = {
  page_content: 'Page',
  chrome: 'Header/Footer',
  page_template: 'Layout',
  business_data: 'Business info',
};

// ------- Helpers for Components tab -------

const detectImageSlots = (html: string): string[] => {
  const pattern = /<!-- APEXOS:IMAGE:(\w+(?::\d+)?) -->/g;
  const slots: string[] = [];
  let match;
  while ((match = pattern.exec(html)) !== null) {
    slots.push(match[1]);
  }
  return slots;
};

const formatSlotLabel = (slot: string): string => {
  if (slot.includes(':')) {
    const [name, num] = slot.split(':');
    return `${name.charAt(0).toUpperCase() + name.slice(1)} Photo ${num}`;
  }
  return `${slot.charAt(0).toUpperCase() + slot.slice(1)} Image`;
};

// ------- Component -------

const WebsiteManager: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [routeParams] = useSearchParams();
  const [pageSearch, setPageSearch] = useState('');
  const [pageTypeFilter, setPageTypeFilter] = useState('');
  const [pageStateFilter, setPageStateFilter] = useState('');

  // Selection state
  const [selectedPageId, setSelectedPageId] = useState<number | null>(() => Number(routeParams.get('page')) || null);
  const [sharedMode, setSharedMode] = useState(false);
  const [activeTab, setActiveTab] = useState(routeParams.get('tab') === 'seo' ? 3 : 0);
  const [previewDevice, setPreviewDevice] = useState<'desktop' | 'tablet' | 'mobile'>('desktop');

  // Page editor local state
  const [metaTitle, setMetaTitle] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [jsonLd, setJsonLd] = useState('');
  const [pageHtml, setPageHtml] = useState('');
  const [pageCss, setPageCss] = useState('');

  // Shared components (site chrome) local state
  const [sharedHeaderHtml, setSharedHeaderHtml] = useState('');
  const [sharedFooterHtml, setSharedFooterHtml] = useState('');
  const [childHeroHtml, setChildHeroHtml] = useState('');

  // Confirmation dialogs
  const [confirmDeployOpen, setConfirmDeployOpen] = useState(routeParams.get('publish') === '1');
  const [addPageOpen, setAddPageOpen] = useState(false);
  // Page created from the Add page dialog whose nav links land once it is written
  const pendingNavPageRef = useRef<number | null>(null);
  const lastPageStatusRef = useRef<string | null>(null);
  const [confirmRepublishOpen, setConfirmRepublishOpen] = useState(false);

  // Dirty tracking
  const [isDirty, setIsDirty] = useState(false);
  const initialValuesRef = useRef<Record<string, string>>({});
  const pageBaselineRef = useRef<PageDraft | null>(null);
  const pageVersionRef = useRef('');
  const [saveConflict, setSaveConflict] = useState(false);

  // Preview iframe key for forcing refresh
  const [iframeKey, setIframeKey] = useState(0);
  // Plain Save writes the DB but not the preview S3 objects
  const [previewStale, setPreviewStale] = useState(false);

  // Component config state
  const [componentConfig, setComponentConfig] = useState<Record<string, any>>({});

  // Edits tab: scope-routed AI edit state — the backend persists per-edit
  // status on a revision row; we poll it instead of guessing from updated_at
  const [editFeedback, setEditFeedback] = useState('');
  const [activeRevisionId, setActiveRevisionId] = useState<number | null>(null);
  const [siteWidePublishHint, setSiteWidePublishHint] = useState(false);
  const reviseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isRevisingPage = activeRevisionId !== null;

  // SEO tab: FAQ structured-data generation state (same polling pattern)
  const [isGeneratingFaqs, setIsGeneratingFaqs] = useState(false);
  const faqBaselineRef = useRef<string | null>(null);
  const faqTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ------- Data fetching -------

  const { data: project, isLoading: projectLoading } = useQuery({
    queryKey: ['website-builder-project', projectId],
    queryFn: async () => {
      const response = await api.websiteBuilder.getProject(projectId!);
      return response.data;
    },
    enabled: !!projectId,
    // Poll while a custom domain is progressing toward live
    refetchInterval: (query) => {
      const domainStatus = query.state.data?.domain_status;
      return domainStatus === 'pending_dns' || domainStatus === 'attaching' ? 30000 : false;
    },
  });

  const { data: pageDetail, isLoading: pageLoading } = useQuery({
    queryKey: ['website-builder-page', projectId, selectedPageId],
    queryFn: async () => {
      const response = await api.websiteBuilder.getPage(projectId!, selectedPageId!);
      return response.data;
    },
    enabled: !!projectId && !!selectedPageId && !sharedMode,
    // While an AI edit, FAQ generation, or a newly added page's first write
    // is running, poll until the page settles
    refetchInterval: (query) => {
      const pageStatus = query.state.data?.status;
      if (isRevisingPage || isGeneratingFaqs) return 4000;
      // Newly added / retried pages: poll until they settle, capped at ~10 min
      // so a page stuck in 'generating' does not poll forever
      if ((pageStatus === 'generating' || pageStatus === 'pending') && query.state.dataUpdateCount < 150) return 4000;
      return false;
    },
  });

  // When a page being written settles, the sidebar/preview (derived from the
  // project query) must refresh too — and the nav-link publish hint applies
  useEffect(() => {
    const status = pageDetail?.status ?? null;
    const previous = lastPageStatusRef.current;
    lastPageStatusRef.current = status;
    if (!pageDetail || previous === null || previous === status) return;
    if ((previous === 'generating' || previous === 'pending') && (status === 'generated' || status === 'failed')) {
      queryClient.invalidateQueries({ queryKey: ['website-builder-project', projectId] });
      if (status === 'failed') {
        toast.error(`"${pageDetail.title}" could not be written — retry it from the project page`);
      } else if (pendingNavPageRef.current === pageDetail.id) {
        setSiteWidePublishHint(true);
        toast.success(`"${pageDetail.title}" is ready — publish the site to put it live`);
      }
      if (pendingNavPageRef.current === pageDetail.id) pendingNavPageRef.current = null;
    }
  }, [pageDetail, projectId, queryClient]);

  // Revision history for the Edits tab (project-wide; filtered per page)
  const { data: revisions } = useQuery({
    queryKey: ['website-builder-revisions', projectId],
    queryFn: async () => {
      const response = await api.websiteBuilder.listRevisions(projectId!);
      return response.data;
    },
    enabled: !!projectId,
  });
  const pageRevisions = (revisions || []).filter((rev) => rev.page === selectedPageId);

  // Poll the in-flight AI edit's revision row until it reaches a terminal state
  const { data: activeRevision } = useQuery({
    queryKey: ['website-builder-revision', projectId, activeRevisionId],
    queryFn: async () => {
      const response = await api.websiteBuilder.getRevision(projectId!, activeRevisionId!);
      return response.data;
    },
    enabled: !!projectId && activeRevisionId !== null,
    refetchInterval: 3000,
  });

  const { data: componentOptions } = useQuery({
    queryKey: ['website-builder', projectId, 'component-options'],
    queryFn: async () => {
      const response = await api.websiteBuilder.componentOptions(projectId!);
      return response.data;
    },
    enabled: !!projectId,
  });

  // Poll deployment progress while the project is deploying
  const { data: deployStatus } = useQuery({
    queryKey: ['website-builder-status', projectId],
    queryFn: async () => {
      const response = await api.websiteBuilder.getStatus(projectId!);
      return response.data;
    },
    enabled: !!projectId && project?.status === 'deploying',
    refetchInterval: 4000,
  });

  useEffect(() => {
    if (!deployStatus || !project || deployStatus.status === project.status) return;
    queryClient.invalidateQueries({ queryKey: ['website-builder-project', projectId] });
    if (deployStatus.status === 'deployed') {
      toast.success('Website deployed to production!');
    } else if (deployStatus.status === 'failed') {
      toast.error(deployStatus.last_error || 'Deployment failed');
    }
  }, [deployStatus?.status]);

  // ------- Initialize local state from fetched data -------

  // Tracks the (page id : updated_at) we last loaded into the editors, so a
  // BACKGROUND refetch (the 4s poll during an AI edit / FAQ generation)
  // doesn't re-hydrate on every tick and silently discard the user's
  // unsaved edits. We re-hydrate on a page SWITCH, or when the content
  // genuinely changed AND the editors aren't dirty.
  const loadedPageKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!pageDetail || sharedMode) return;
    const key = `${pageDetail.id}:${pageDetail.updated_at}`;
    if (key === loadedPageKeyRef.current) return; // nothing new to load
    const isPageSwitch =
      !loadedPageKeyRef.current ||
      !loadedPageKeyRef.current.startsWith(`${pageDetail.id}:`);
    // Same page, new server content, but the user has unsaved edits →
    // preserve their edits (they can save or discard); don't clobber.
    if (!isPageSwitch && isDirty) return;

    // json_ld is a backend JSONField — normally an object, but may be a
    // string after manual edits. Always edit it as text.
    const rawJsonLd = pageDetail.json_ld;
    const jsonLdText = typeof rawJsonLd === 'string'
      ? rawJsonLd
      : (rawJsonLd && Object.keys(rawJsonLd).length > 0
        ? JSON.stringify(rawJsonLd, null, 2)
        : '');
    const vals = {
      metaTitle: pageDetail.meta_title || '',
      metaDescription: pageDetail.meta_description || '',
      jsonLd: jsonLdText,
      html: pageDetail.html || '',
      css: pageDetail.css || '',
    };
    setMetaTitle(vals.metaTitle);
    setMetaDescription(vals.metaDescription);
    setJsonLd(vals.jsonLd);
    setPageHtml(vals.html);
    setPageCss(vals.css);
    initialValuesRef.current = vals;
    pageBaselineRef.current = { ...vals, componentConfig: pageDetail.component_config || {} };
    pageVersionRef.current = pageDetail.updated_at;
    setComponentConfig(pageDetail.component_config || {});
    setSaveConflict(false);
    loadedPageKeyRef.current = key;
    setIsDirty(false);
  }, [pageDetail, sharedMode, isDirty]);

  useEffect(() => {
    if (pageDetail) {
      setComponentConfig(pageDetail.component_config || {});
    }
  }, [pageDetail?.id]);

  useEffect(() => {
    if (project && sharedMode && !isDirty) {
      const vals = {
        sharedHeaderHtml: project.shared_header_html || '',
        sharedFooterHtml: project.shared_footer_html || '',
        childHeroHtml: project.child_hero_html || '',
      };
      setSharedHeaderHtml(vals.sharedHeaderHtml);
      setSharedFooterHtml(vals.sharedFooterHtml);
      setChildHeroHtml(vals.childHeroHtml);
      initialValuesRef.current = vals;
      setIsDirty(false);
    }
  }, [project, sharedMode]);

  // Auto-select first page
  useEffect(() => {
    if (project?.pages?.length && !selectedPageId && !sharedMode) {
      setSelectedPageId(project.pages[0].id);
    }
  }, [project]);

  // ------- Dirty detection -------

  const checkDirty = useCallback(() => {
    if (sharedMode) {
      const dirty =
        sharedHeaderHtml !== (initialValuesRef.current.sharedHeaderHtml ?? '') ||
        sharedFooterHtml !== (initialValuesRef.current.sharedFooterHtml ?? '') ||
        childHeroHtml !== (initialValuesRef.current.childHeroHtml ?? '');
      setIsDirty(dirty);
    } else {
      const dirty =
        metaTitle !== (initialValuesRef.current.metaTitle ?? '') ||
        metaDescription !== (initialValuesRef.current.metaDescription ?? '') ||
        jsonLd !== (initialValuesRef.current.jsonLd ?? '') ||
        pageHtml !== (initialValuesRef.current.html ?? '') ||
        pageCss !== (initialValuesRef.current.css ?? '') ||
        JSON.stringify(componentConfig) !== JSON.stringify(pageBaselineRef.current?.componentConfig || {});
      setIsDirty(dirty);
    }
  }, [sharedMode, sharedHeaderHtml, sharedFooterHtml, childHeroHtml, metaTitle, metaDescription, jsonLd, pageHtml, pageCss, componentConfig]);

  useEffect(() => {
    checkDirty();
  }, [checkDirty]);

  // ------- Component config helpers -------

  const updateComponentConfig = (key: string, value: any) => {
    setComponentConfig(prev => {
      const updated = { ...prev, [key]: value === 'default' ? undefined : value };
      Object.keys(updated).forEach(k => updated[k] === undefined && delete updated[k]);
      return updated;
    });
    setIsDirty(true);
  };

  const updateImageSlot = (slot: string, photo: any) => {
    setComponentConfig(prev => ({
      ...prev,
      images: {
        ...(prev.images || {}),
        [slot]: { photo_id: photo.id, url: photo.url, name: photo.name, alt_text: photo.alt_text || photo.name, focal_x: 50, focal_y: 50 },
      },
    }));
    setIsDirty(true);
  };

  const clearImageSlot = (slot: string) => {
    setComponentConfig(prev => {
      const images = { ...(prev.images || {}) };
      delete images[slot];
      return { ...prev, images };
    });
    setIsDirty(true);
  };

  const isSelectedForSlot = (slot: string, photo: any): boolean => {
    // Match by URL (always present and unique); photos from the media
    // library may have no id, and `undefined === undefined` made EVERY
    // thumbnail render as selected when no photo was pinned.
    const chosen = componentConfig?.images?.[slot];
    if (!chosen || !chosen.url || !photo.url) return false;
    return chosen.url === photo.url;
  };

  const imageSlots = pageHtml ? detectImageSlots(pageHtml) : [];

  // ------- Page switching with dirty confirmation -------

  const handleOpenAddPage = () => {
    if (isDirty && !window.confirm('You have unsaved changes. Discard them and add a page?')) return;
    setAddPageOpen(true);
  };

  const handlePageCreated = (result: WebsitePageCreateResponse) => {
    queryClient.invalidateQueries({ queryKey: ['website-builder-project', projectId] });
    setSharedMode(false);
    setSelectedPageId(result.page.id);
    setActiveTab(0);
    setIsDirty(false);
    toast.success(`Writing "${result.page.title}" — it will appear here in a minute`);
    if (result.nav_requested) pendingNavPageRef.current = result.page.id;
  };

  const navToggleMutation = useMutation({
    mutationFn: async (payload: { show_in_header?: boolean; show_in_footer?: boolean }) =>
      (await api.websiteBuilder.updatePage(projectId!, selectedPageId!, { ...payload, base_updated_at: pageDetail!.updated_at })).data,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['website-builder-page', projectId, selectedPageId] });
      queryClient.invalidateQueries({ queryKey: ['website-builder-project', projectId] });
      if (data.nav_changed) {
        setSiteWidePublishHint(true);
        toast.success('Navigation updated on every page — publish the site to push it live');
      } else {
        toast('No navigation link to change for this page');
      }
    },
    onError: (error) => toast.error(apiErrorMessage(error, 'Could not update the navigation')),
  });

  const handleSelectPage = (pageId: number) => {
    if (savePage.isPending || saveShared.isPending) { toast('Wait for the current save to finish.'); return; }
    if (isDirty) {
      if (!window.confirm('You have unsaved changes. Discard and switch pages?')) return;
    }
    setSharedMode(false);
    setSelectedPageId(pageId);
    setActiveTab(0);
  };

  const handleSelectShared = () => {
    if (isDirty) {
      if (!window.confirm('You have unsaved changes. Discard and switch to shared components?')) return;
    }
    setSharedMode(true);
    setSelectedPageId(null);
    setActiveTab(0);
  };

  const goToGlobalSettings = () => {
    if (isDirty) {
      if (!window.confirm('You have unsaved changes. Discard and open Global Settings?')) return;
    }
    navigate(`/marketing/website-builder/${projectId}/settings`);
  };

  // ------- Mutations -------

  const apiErrorMessage = (error: unknown, fallback: string): string => {
    const err = error as { responseData?: { error?: string; detail?: string } };
    return err?.responseData?.error || err?.responseData?.detail || fallback;
  };

  const canVisuallyEdit =
    project?.pages?.find((p) => p.id === selectedPageId)?.has_structured_content !== false;
  const isTemplateProject =
    !!project?.page_templates && Object.keys(project.page_templates).length > 0;

  const savePage = useMutation({
    mutationFn: async () => {
      if (!pageBaselineRef.current) throw new Error('Wait for the page to load.');
      const draft: PageDraft = { metaTitle, metaDescription, jsonLd, html: pageHtml, css: pageCss, componentConfig };
      const payload = pagePatch(draft, pageBaselineRef.current, pageVersionRef.current);
      const { data } = await api.websiteBuilder.updatePage(projectId!, selectedPageId!, payload);
      return { data, draft };
    },
    onSuccess: ({ data, draft }) => {
      pageVersionRef.current = data.updated_at;
      pageBaselineRef.current = draft;
      initialValuesRef.current = { metaTitle: draft.metaTitle, metaDescription: draft.metaDescription, jsonLd: draft.jsonLd, html: draft.html, css: draft.css };
      // Keep edits typed while this save was in flight dirty.
      const changed = metaTitle !== draft.metaTitle || metaDescription !== draft.metaDescription || jsonLd !== draft.jsonLd || pageHtml !== draft.html || pageCss !== draft.css || JSON.stringify(componentConfig) !== JSON.stringify(draft.componentConfig);
      setIsDirty(changed);
      setSaveConflict(false);
      toast.success('Page saved successfully');
      queryClient.invalidateQueries({ queryKey: ['website-builder-page', projectId, selectedPageId] });
      queryClient.invalidateQueries({ queryKey: ['website-builder-project', projectId] });
      setPreviewStale(true);
    },
    onError: (error) => {
      if ((error as { status?: number }).status === 409) setSaveConflict(true);
      toast.error(apiErrorMessage(error, error instanceof Error ? error.message : 'Failed to save page'));
    },
  });

  const saveShared = useMutation({
    mutationFn: async () => {
      await api.websiteBuilder.updateProject(projectId!, {
        shared_header_html: sharedHeaderHtml,
        shared_footer_html: sharedFooterHtml,
        child_hero_html: childHeroHtml,
      });
    },
    onSuccess: () => {
      toast.success('Site chrome saved');
      // Update initial values so dirty tracking stays correct after refetch
      initialValuesRef.current = {
        ...initialValuesRef.current,
        sharedHeaderHtml,
        sharedFooterHtml,
        childHeroHtml,
      };
      queryClient.invalidateQueries({ queryKey: ['website-builder-project', projectId] });
      setIsDirty(false);
      // Offer to propagate the edited chrome to every page (free, no AI)
      if (isTemplateProject) {
        setConfirmRepublishOpen(true);
      }
    },
    onError: (error) => {
      toast.error(apiErrorMessage(error, 'Failed to save site chrome'));
    },
  });

  const republishChrome = useMutation({
    mutationFn: async () => {
      const response = await api.websiteBuilder.republishChrome(projectId!, {
        redeploy: project?.status === 'deployed',
      });
      return response.data;
    },
    onSuccess: () => {
      setConfirmRepublishOpen(false);
      toast.success('Publishing header/footer to all pages — the preview will refresh shortly');
      queryClient.invalidateQueries({ queryKey: ['website-builder-project', projectId] });
    },
    onError: (error) => {
      setConfirmRepublishOpen(false);
      const err = error as { status?: number };
      if (err?.status === 409) {
        toast.error('Cannot republish while generation is in progress — try again shortly');
        return;
      }
      toast.error(apiErrorMessage(error, 'Failed to republish header/footer'));
    },
  });

  const refreshPreview = useMutation({
    mutationFn: async () => {
      await api.websiteBuilder.refreshPreview(projectId!);
    },
    onSuccess: () => {
      toast.success('Preview refreshed');
      setPreviewStale(false);
      setIframeKey((k) => k + 1);
      queryClient.invalidateQueries({ queryKey: ['website-builder-project', projectId] });
    },
    onError: (error) => {
      toast.error(apiErrorMessage(error, 'Failed to refresh preview'));
    },
  });

  const deployProject = useMutation({
    mutationFn: async () => {
      await api.websiteBuilder.deploy(projectId!);
    },
    onSuccess: () => {
      setConfirmDeployOpen(false);
      toast.success('Deploying to production...');
      queryClient.invalidateQueries({ queryKey: ['website-builder-project', projectId] });
    },
    onError: (error) => {
      setConfirmDeployOpen(false);
      toast.error(apiErrorMessage(error, 'Deployment failed to start'));
    },
  });

  // AI edit (Edits tab): dispatch, then poll the revision row's status —
  // failures and no-op edits are real states, not silence
  const revisePageMutation = useMutation({
    mutationFn: async (force: boolean) => {
      const response = await api.websiteBuilder.aiEdit(projectId!, {
        feedback: editFeedback.trim(),
        page_id: selectedPageId,
        force,
      });
      return response.data;
    },
    onSuccess: (data) => {
      setActiveRevisionId(data.revision_id);
      // Safety: stop waiting after 5 minutes
      if (reviseTimeoutRef.current) clearTimeout(reviseTimeoutRef.current);
      reviseTimeoutRef.current = setTimeout(() => {
        setActiveRevisionId(null);
        toast.error('The edit is taking longer than expected — refresh in a minute to see if it landed.');
      }, 5 * 60 * 1000);
    },
    onError: (error) => {
      const data = (error as { responseData?: { code?: string; error?: string } })?.responseData;
      if (data?.code === 'manual_edits_present') {
        if (window.confirm(
          'This page\'s HTML was edited by hand. An AI edit rebuilds the page and '
          + 'discards those manual changes. Apply the AI edit anyway?',
        )) {
          revisePageMutation.mutate(true);
        }
        return;
      }
      toast.error(apiErrorMessage(error, 'Failed to start the edit'));
    },
  });

  // Terminal-state handling for AI edits
  useEffect(() => {
    if (!activeRevision || activeRevision.id !== activeRevisionId) return;
    if (!['succeeded', 'failed', 'no_change'].includes(activeRevision.status)) return;

    setActiveRevisionId(null);
    if (reviseTimeoutRef.current) clearTimeout(reviseTimeoutRef.current);
    queryClient.invalidateQueries({ queryKey: ['website-builder-revisions', projectId] });

    if (activeRevision.status === 'failed') {
      toast.error(activeRevision.error || 'The edit failed — try rephrasing your instruction.');
      return;
    }
    if (activeRevision.status === 'no_change') {
      toast(activeRevision.result_summary
        || 'The AI made no changes for that instruction — try being more specific.');
      return;
    }

    setEditFeedback('');
    const siteWide = ['chrome', 'page_template', 'business_data'].includes(activeRevision.scope);
    toast.success(
      activeRevision.result_summary
      || (siteWide ? 'Applied across all pages — the preview is refreshing' : 'Edit applied — the preview is refreshing'),
    );
    // The preview upload runs just after assembly; refresh the iframe now
    // and once more when the upload has had a moment to land
    setIframeKey((k) => k + 1);
    setTimeout(() => setIframeKey((k) => k + 1), 4000);
    queryClient.invalidateQueries({ queryKey: ['website-builder-project', projectId] });
    queryClient.invalidateQueries({ queryKey: ['website-builder-page', projectId, selectedPageId] });
    if (siteWide && project?.status === 'deployed') setSiteWidePublishHint(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRevision, activeRevisionId]);

  // SEO tab: generate FAQPage structured data (background AI call; poll
  // updated_at like the Edits tab does)
  const generateFaqsMutation = useMutation({
    mutationFn: async () => {
      const response = await api.websiteBuilder.generatePageFaqs(projectId!, selectedPageId!);
      return response.data;
    },
    onSuccess: () => {
      faqBaselineRef.current = pageDetail?.updated_at || null;
      setIsGeneratingFaqs(true);
      if (faqTimeoutRef.current) clearTimeout(faqTimeoutRef.current);
      faqTimeoutRef.current = setTimeout(() => {
        setIsGeneratingFaqs(false);
        toast.error('FAQ generation is taking longer than expected — check back in a minute.');
      }, 3 * 60 * 1000);
    },
    onError: (error) => {
      toast.error(apiErrorMessage(error, 'Failed to start FAQ generation'));
    },
  });

  useEffect(() => {
    if (!isGeneratingFaqs || !pageDetail?.updated_at) return;
    if (faqBaselineRef.current && pageDetail.updated_at !== faqBaselineRef.current) {
      setIsGeneratingFaqs(false);
      if (faqTimeoutRef.current) clearTimeout(faqTimeoutRef.current);
      toast.success('Visible FAQs and matching structured data added to this page');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGeneratingFaqs, pageDetail?.updated_at]);

  // Legal pages: record consent to the stubbed content (unlocks publishing)
  const approveLegalMutation = useMutation({
    mutationFn: async () => {
      const response = await api.websiteBuilder.approveLegalPage(projectId!, selectedPageId!);
      return response.data;
    },
    onSuccess: (data) => {
      toast.success(`Approved — publishing unlocked (recorded for ${data.approved_by})`);
      queryClient.invalidateQueries({ queryKey: ['website-builder-page', projectId, selectedPageId] });
      queryClient.invalidateQueries({ queryKey: ['website-builder-project', projectId] });
    },
    onError: (error) => {
      toast.error(apiErrorMessage(error, 'Could not record the approval'));
    },
  });

  // Publish just the selected page to the live site (saves first if dirty)
  const publishPage = useMutation({
    mutationFn: async () => {
      if (isDirty) {
        await savePage.mutateAsync();
      }
      const response = await api.websiteBuilder.deployPage(projectId!, selectedPageId!);
      return response.data;
    },
    onSuccess: (data) => {
      toast.success(data.message || 'Publishing this page to the live site');
    },
    onError: (error) => {
      toast.error(apiErrorMessage(error, 'Failed to publish this page'));
    },
  });

  const handleSave = () => {
    if (sharedMode) {
      saveShared.mutate();
    } else {
      savePage.mutate();
    }
  };

  const handleSaveAndPreview = async () => {
    if (sharedMode) {
      saveShared.mutate(undefined, {
        onSuccess: () => refreshPreview.mutate(),
      });
    } else {
      savePage.mutate(undefined, {
        onSuccess: () => refreshPreview.mutate(),
      });
    }
  };

  // ------- Preview dimensions -------

  const previewWidth = previewDevice === 'mobile' ? 375 : previewDevice === 'tablet' ? 768 : 1440;

  // ------- Current page info -------

  const filteredPages = filterWebsitePages(project?.pages || [], pageSearch, pageTypeFilter, pageStateFilter, selectedPageId);
  const currentPage = project?.pages?.find((p) => p.id === selectedPageId);
  const canAddPages = !!project && (project.status === 'site_review' || project.status === 'deployed')
    && project.generation_method === 'template';
  const currentPreviewUrl = currentPage?.preview_url || project?.preview_url;

  // ------- Loading state -------

  if (projectLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!project) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography level="h3">Project not found</Typography>
        <Button
          variant="outlined"
          startDecorator={<ArrowBackIcon />}
          onClick={() => navigate('/marketing/website-builder')}
          sx={{ mt: 2 }}
        >
          Back to Website Builder
        </Button>
      </Box>
    );
  }

  // ------- Render -------

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        // Desktop: app-like fixed height with inner scrolling panes.
        // Mobile: natural page flow — panes stack and the page scrolls.
        height: { xs: 'auto', md: 'calc(100vh - 64px)' },
        minHeight: { xs: 'calc(100dvh - 64px)', md: 'auto' },
      }}
    >
      {saveConflict && <Alert color="warning" sx={{ borderRadius: 0 }}>
        A newer draft exists. Your edits are still here. Open this page in another tab to compare, or reload to discard your local changes.
        <Button size="sm" variant="outlined" onClick={() => window.open(window.location.href, '_blank', 'noopener,noreferrer')}>Compare latest</Button>
        <Button size="sm" onClick={() => { if (window.confirm('Discard local edits and load the latest saved draft?')) { loadedPageKeyRef.current = ''; setIsDirty(false); setSaveConflict(false); queryClient.invalidateQueries({ queryKey: ['website-builder-page', projectId, selectedPageId] }); } }}>Reload latest</Button>
      </Alert>}
      {/* Top action bar */}
      <Sheet
        variant="outlined"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
          px: { xs: 1, sm: 2 },
          py: 1,
          borderLeft: 0,
          borderRight: 0,
          borderTop: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <IconButton
            variant="plain"
            size="sm"
            onClick={() => navigate(`/marketing/website-builder/${projectId}`)}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography level="title-md" noWrap sx={{ minWidth: 0 }}>{project.name}</Typography>
          {isDirty && (
            <Chip size="sm" variant="soft" color="warning">
              Unsaved changes
            </Chip>
          )}
          {!isDirty && previewStale && (
            <Chip size="sm" variant="soft" color="neutral" title="Saved, but the preview shows the last uploaded version — use Save & Preview to refresh it">
              Preview outdated
            </Chip>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {project.status === 'deployed' && project.production_url && (
            <Button
              variant="plain"
              size="sm"
              endDecorator={<OpenInNewIcon />}
              onClick={() => {
                const slug = currentPage?.slug?.replace(/^\/+|\/+$/g, '') || '';
                const base = project.production_url.replace(/\/$/, '');
                window.open(slug && !sharedMode ? `${base}/${slug}/` : base, '_blank');
              }}
            >
              View Live Site
            </Button>
          )}
          <Button
            variant="outlined"
            color="primary"
            size="sm"
            startDecorator={<SettingsIcon />}
            onClick={goToGlobalSettings}
          >
            Global Settings
          </Button>
          {isTemplateProject && !sharedMode && selectedPageId && ['site_review', 'deployed'].includes(project.status) && (
            <Tooltip
              title={
                canVisuallyEdit
                  ? 'Click anything on the page and edit it in place'
                  : 'This page was generated before structured content was stored — regenerate it to use the visual editor'
              }
            >
              <span>
                <Button
                  variant="outlined"
                  color="primary"
                  size="sm"
                  disabled={!canVisuallyEdit}
                  startDecorator={<BrushIcon />}
                  onClick={() => {
                    if (isDirty && !window.confirm('You have unsaved changes here. Open the Visual Editor without saving them?')) return;
                    navigate(`/marketing/website-builder/${projectId}/visual/${selectedPageId}`);
                  }}
                >
                  {pageDetail && LEGAL_PAGE_TYPES.includes(pageDetail.page_type) ? 'Edit legal text' : 'Visual Editor'}
                </Button>
              </span>
            </Tooltip>
          )}
          <Button
            variant="outlined"
            color="primary"
            size="sm"
            startDecorator={<SaveIcon />}
            onClick={handleSave}
            loading={savePage.isPending || saveShared.isPending}
            disabled={!isDirty}
          >
            Save
          </Button>
          <Button
            variant="outlined"
            color="primary"
            size="sm"
            startDecorator={<RefreshIcon />}
            onClick={handleSaveAndPreview}
            loading={refreshPreview.isPending}
          >
            Save & Preview
          </Button>
          {project.status === 'deployed' && !sharedMode && selectedPageId && (
            <Button
              variant="soft"
              size="sm"
              color="success"
              startDecorator={<RocketIcon />}
              onClick={() => publishPage.mutate()}
              loading={publishPage.isPending}
              title="Publish just this page to the live site (fast — no full redeploy)"
            >
              Publish This Page
            </Button>
          )}
          <Button
            variant="solid"
            size="sm"
            color="success"
            startDecorator={<RocketIcon />}
            onClick={() => setConfirmDeployOpen(true)}
            loading={deployProject.isPending || project.status === 'deploying'}
          >
            {project.status === 'deploying' ? 'Deploying...' : 'Deploy'}
          </Button>
        </Box>
      </Sheet>

      {/* Business data changed since the last publish — live site is stale */}
      {project.status === 'deployed' && project.business_in_sync === false && (
        <Alert
          color="warning"
          variant="soft"
          sx={{ borderRadius: 0, alignItems: 'center', flexWrap: 'wrap', gap: 1 }}
        >
          <Box sx={{ flex: 1, minWidth: 240 }}>
            <Typography level="title-sm">Your business phone number changed</Typography>
            <Typography level="body-sm">
              The live site still shows the old number
              {project.live_business_info?.phone ? (
                <> — it should be <strong>{project.live_business_info.phone}</strong></>
              ) : null}
              . One click updates every page (header, footer, and buttons included) and publishes the site.
            </Typography>
          </Box>
          <Button
            color="warning"
            variant="solid"
            size="sm"
            startDecorator={<RocketIcon />}
            loading={republishChrome.isPending}
            onClick={() => republishChrome.mutate()}
          >
            Update &amp; Publish
          </Button>
        </Alert>
      )}

      {/* Pre-publish validation issues (report-only — deploy is not blocked) */}
      {project.validation_report?.passed === false && (
        <Alert color="warning" variant="soft" sx={{ borderRadius: 0, alignItems: 'flex-start' }}>
          <Box>
            <Typography level="title-sm" sx={{ mb: 0.5 }}>
              Pre-publish checks found issues
            </Typography>
            <List size="sm" marker="disc" sx={{ '--List-padding': '0px', '--ListItem-minHeight': '1.5rem' }}>
              {(project.validation_report.errors || []).slice(0, 5).map((issue, i) => (
                <ListItem key={i}>
                  <Typography level="body-xs">
                    {issue.page ? `${issue.page}: ` : ''}{issue.message}
                  </Typography>
                </ListItem>
              ))}
            </List>
          </Box>
        </Alert>
      )}

      <Box sx={{ px: 2, py: 1, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        <Input size="sm" placeholder="Search pages or URLs" value={pageSearch} onChange={event => setPageSearch(event.target.value)} />
        <Select size="sm" aria-label="Filter page type" value={pageTypeFilter} onChange={(_, value) => setPageTypeFilter(value || '')}><Option value="">All page types</Option>{Array.from(new Set(project.pages.map(page => page.page_type))).sort().map(value => <Option key={value} value={value}>{value.replaceAll('_', ' ')}</Option>)}</Select>
        <Select size="sm" aria-label="Filter page status" value={pageStateFilter} onChange={(_, value) => setPageStateFilter(value || '')}><Option value="">All statuses</Option><Option value="unpublished">Unpublished</Option><Option value="failed">Failed</Option><Option value="pending">Pending</Option></Select>
        <Typography level="body-xs">{filteredPages.length} of {project.pages.length} pages · selected page stays visible</Typography>
        <Button size="sm" variant="outlined" onClick={() => { if (!isDirty || window.confirm('Discard unsaved edits and open the workspace?')) navigate(`/marketing/website-builder/${projectId}/workspace`); }}>Launch, review & SEO</Button>
      </Box>
      {/* Mobile page picker (replaces the sidebar below md) */}
      <Sheet
        variant="outlined"
        sx={{ display: { xs: 'block', md: 'none' }, p: 1, borderLeft: 0, borderRight: 0, borderTop: 0 }}
      >
        <Select
          size="sm"
          value={sharedMode ? '__shared__' : String(selectedPageId ?? '')}
          onChange={(_e, val) => {
            if (val === '__shared__') handleSelectShared();
            else if (val === '__settings__') goToGlobalSettings();
            else if (val) handleSelectPage(Number(val));
          }}
        >
          {filteredPages.map((page) => (
            <Option key={page.id} value={String(page.id)}>
              {page.title} — /{page.slug}
            </Option>
          ))}
          <Option value="__shared__">Shared Components (Header, Hero &amp; Footer)</Option>
          <Option value="__settings__">Global Settings (Domain, Analytics)</Option>
        </Select>
      </Sheet>

      {/* Main content: sidebar + editor */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          flex: 1,
          overflow: { xs: 'visible', md: 'hidden' },
        }}
      >
        {/* Left sidebar (desktop only — mobile uses the Select above) */}
        <Sheet
          variant="outlined"
          sx={{
            width: 280,
            minWidth: 280,
            display: { xs: 'none', md: 'flex' },
            borderTop: 0,
            borderLeft: 0,
            borderBottom: 0,
            overflow: 'auto',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography level="body-xs" sx={{ textTransform: 'uppercase', fontWeight: 700, color: 'text.tertiary' }}>
              Pages
            </Typography>
            <Button
              size="sm"
              variant="soft"
              startDecorator={<PlusIcon size={14} />}
              onClick={handleOpenAddPage}
              disabled={!canAddPages}
              title={canAddPages ? 'Add a page written by AI from your description' : 'Pages can be added once the site is generated'}
            >
              Add page
            </Button>
          </Box>

          <Box sx={{ flex: 1, overflow: 'auto' }}>
            {filteredPages.map((page) => (
              <Box
                key={page.id}
                onClick={() => handleSelectPage(page.id)}
                sx={{
                  px: 2,
                  py: 1.5,
                  cursor: 'pointer',
                  bgcolor: !sharedMode && selectedPageId === page.id ? 'primary.softBg' : 'transparent',
                  borderLeft: '3px solid',
                  borderColor: !sharedMode && selectedPageId === page.id ? 'primary.500' : 'transparent',
                  '&:hover': {
                    bgcolor: !sharedMode && selectedPageId === page.id ? 'primary.softBg' : 'neutral.softBg',
                  },
                  transition: 'all 0.15s',
                }}
              >
                <Typography level="body-sm" fontWeight={600} noWrap>
                  {page.title}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                  <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                    /{page.slug}
                  </Typography>
                  {page.html_manually_edited && (
                    <Chip
                      size="sm"
                      variant="outlined"
                      color="neutral"
                      sx={{ ml: 'auto', fontSize: '0.65rem' }}
                      title="This page's HTML was edited by hand — AI edits and header/footer republish would overwrite it"
                    >
                      manual edits
                    </Chip>
                  )}
                  {project.status === 'deployed'
                    && page.published_at
                    && page.updated_at > page.published_at && (
                    <Chip
                      size="sm"
                      variant="soft"
                      color="warning"
                      sx={{ ml: page.html_manually_edited ? 0 : 'auto', fontSize: '0.65rem' }}
                      title="Changed since it was last published — the live site shows the older version"
                    >
                      unpublished
                    </Chip>
                  )}
                  {page.legal_approval_valid === false ? (
                    <Chip size="sm" variant="soft" color="warning" sx={{ ml: 'auto', fontSize: '0.65rem' }}>
                      needs approval
                    </Chip>
                  ) : (
                    <Chip
                      size="sm"
                      variant="soft"
                      color={statusColors[page.status] || 'neutral'}
                      sx={{ ml: 'auto', fontSize: '0.65rem' }}
                    >
                      {page.status}
                    </Chip>
                  )}
                </Box>
              </Box>
            ))}
          </Box>

          <Divider />

          <Box
            onClick={handleSelectShared}
            sx={{
              px: 2,
              py: 1.5,
              cursor: 'pointer',
              bgcolor: sharedMode ? 'primary.softBg' : 'transparent',
              borderLeft: '3px solid',
              borderColor: sharedMode ? 'primary.500' : 'transparent',
              '&:hover': {
                bgcolor: sharedMode ? 'primary.softBg' : 'neutral.softBg',
              },
              transition: 'all 0.15s',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SharedIcon size={18} color="var(--joy-palette-text-secondary)" />
              <Typography level="body-sm" fontWeight={600}>
                Shared Components
              </Typography>
            </Box>
            <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 0.25 }}>
              Header, Hero & Footer
            </Typography>
          </Box>

          <Divider />

          <Box
            onClick={goToGlobalSettings}
            sx={{
              px: 2,
              py: 1.5,
              cursor: 'pointer',
              bgcolor: 'transparent',
              borderLeft: '3px solid',
              borderColor: 'transparent',
              '&:hover': { bgcolor: 'neutral.softBg' },
              transition: 'all 0.15s',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SettingsIcon size={18} color="var(--joy-palette-text-secondary)" />
              <Typography level="body-sm" fontWeight={600}>
                Global Settings
              </Typography>
              {project.domain_status === 'live' && (
                <Chip size="sm" variant="soft" color="success" sx={{ ml: 'auto', fontSize: '0.65rem' }}>
                  live
                </Chip>
              )}
              {(project.domain_status === 'pending_dns' || project.domain_status === 'attaching') && (
                <Chip size="sm" variant="soft" color="warning" sx={{ ml: 'auto', fontSize: '0.65rem' }}>
                  pending
                </Chip>
              )}
            </Box>
            <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 0.25 }} noWrap>
              {project.custom_domain || 'Domain, analytics & Search Console'}
            </Typography>
          </Box>
        </Sheet>

        {/* Right main area */}
        <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
          {sharedMode ? (
            // Shared components (site chrome) editor
            <Box sx={{ p: { xs: 2, md: 3 }, flex: 1, overflow: 'auto' }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 3 }}>
                <Typography level="h3">
                  Shared Components
                </Typography>
                <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                  Chrome v{project.chrome_version}
                </Typography>
              </Box>
              <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 3 }}>
                Header, child page hero banner, and footer HTML shared across all pages.
                {isTemplateProject && ' After saving you can publish the changes to every page for free (no AI generation).'}
              </Typography>

              <Stack spacing={3}>
                <Box>
                  <Typography level="title-sm" sx={{ mb: 1 }}>
                    Header HTML
                  </Typography>
                  <CodeEditor
                    value={sharedHeaderHtml}
                    onChange={setSharedHeaderHtml}
                    language="html"
                    height="300px"
                  />
                </Box>
                <Box>
                  <Typography level="title-sm" sx={{ mb: 1 }}>
                    Child Hero Banner
                  </Typography>
                  <CodeEditor
                    value={childHeroHtml}
                    onChange={setChildHeroHtml}
                    language="html"
                    height="300px"
                  />
                </Box>
                <Box>
                  <Typography level="title-sm" sx={{ mb: 1 }}>
                    Footer HTML
                  </Typography>
                  <CodeEditor
                    value={sharedFooterHtml}
                    onChange={setSharedFooterHtml}
                    language="html"
                    height="300px"
                  />
                </Box>
                {isTemplateProject && (
                  <Box>
                    <Button
                      variant="outlined"
                      color="primary"
                      startDecorator={<RefreshIcon />}
                      onClick={() => setConfirmRepublishOpen(true)}
                      loading={republishChrome.isPending}
                      disabled={isDirty}
                    >
                      Publish Header/Footer to All Pages
                    </Button>
                    {isDirty && (
                      <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 0.5 }}>
                        Save your changes first, then publish them to all pages.
                      </Typography>
                    )}
                  </Box>
                )}
              </Stack>
            </Box>
          ) : selectedPageId ? (
            // Page editor with tabs
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {pageLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                {pageDetail && LEGAL_PAGE_TYPES.includes(pageDetail.page_type) && (
                  pageDetail.legal_approval_valid ? (
                    <Alert color="success" variant="soft" sx={{ borderRadius: 0 }}>
                      <Typography level="body-sm">
                        Approved by <strong>{pageDetail.legal_approved_by_name}</strong> on{' '}
                        {pageDetail.legal_approved_at
                          ? new Date(pageDetail.legal_approved_at).toLocaleString()
                          : ''}
                        . Editing this page will require re-approval before it can publish.
                      </Typography>
                    </Alert>
                  ) : (
                    <Alert
                      color="warning"
                      variant="soft"
                      sx={{ borderRadius: 0, alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}
                    >
                      <Box sx={{ flex: 1, minWidth: 240 }}>
                        <Typography level="title-sm">Legal page — review required</Typography>
                        <Typography level="body-sm">
                          This is starter {pageDetail.page_type === 'terms' ? 'Terms of Use' : 'Privacy Policy'} text
                          with your company details filled in. Use Edit legal text above to change it, then save and review it — your site
                          cannot publish until you approve it. Your name and a timestamp are recorded with
                          the exact content you approve.
                        </Typography>
                      </Box>
                      <Button
                        color="warning"
                        variant="solid"
                        size="sm"
                        loading={approveLegalMutation.isPending}
                        disabled={isDirty || savePage.isPending}
                        onClick={() => {
                          if (window.confirm(
                            'By approving, you confirm you have reviewed this page\'s content and '
                            + 'authorize publishing it on your website. Your user, the date/time, and '
                            + 'the approved content are recorded.',
                          )) approveLegalMutation.mutate();
                        }}
                      >
                        Approve &amp; Unlock Publishing
                      </Button>
                    </Alert>
                  )
                )}
                <Tabs
                  value={activeTab}
                  onChange={(_e, val) => setActiveTab(val as number)}
                  sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
                >
                  <TabList
                    sx={{
                      px: { xs: 0.5, sm: 2 },
                      flexWrap: 'wrap',
                      '& > *': { flex: { xs: '1 1 30%', sm: '0 0 auto' } },
                    }}
                  >
                    <Tab>
                      <PreviewIcon size={18} style={{ marginRight: 4 }} />
                      Preview
                    </Tab>
                    <Tab>
                      <EditsIcon size={18} style={{ marginRight: 4 }} />
                      Edits
                    </Tab>
                    <Tab>
                      <ComponentsIcon size={18} style={{ marginRight: 4 }} />
                      Components
                    </Tab>
                    <Tab>
                      <SeoIcon size={18} style={{ marginRight: 4 }} />
                      SEO
                    </Tab>
                    <Tab>
                      <CodeIcon size={18} style={{ marginRight: 4 }} />
                      HTML
                    </Tab>
                    <Tab>
                      <CodeIcon size={18} style={{ marginRight: 4 }} />
                      CSS
                    </Tab>
                  </TabList>

                  {/* Preview Tab */}
                  <TabPanel value={0} sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                    {/* Device toggles */}
                    <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Button
                        variant={previewDevice === 'desktop' ? 'solid' : 'outlined'}
                        size="sm"
                        startDecorator={<DesktopIcon />}
                        onClick={() => setPreviewDevice('desktop')}
                      >
                        Desktop
                      </Button>
                      <Button
                        variant={previewDevice === 'tablet' ? 'solid' : 'outlined'}
                        size="sm"
                        startDecorator={<TabletIcon />}
                        onClick={() => setPreviewDevice('tablet')}
                      >
                        Tablet
                      </Button>
                      <Button
                        variant={previewDevice === 'mobile' ? 'solid' : 'outlined'}
                        size="sm"
                        startDecorator={<MobileIcon />}
                        onClick={() => setPreviewDevice('mobile')}
                      >
                        Mobile
                      </Button>

                      <Box sx={{ ml: 'auto' }}>
                        {currentPreviewUrl && (
                          <Button
                            variant="plain"
                            size="sm"
                            endDecorator={<OpenInNewIcon />}
                            onClick={() => window.open(currentPreviewUrl, '_blank')}
                          >
                            Open in New Tab
                          </Button>
                        )}
                      </Box>
                    </Box>

                    {/* iframe container */}
                    <Box
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 'md',
                        overflow: 'hidden',
                        height: { xs: 'calc(100dvh - 260px)', md: 700 },
                        minHeight: 420,
                        bgcolor: '#ffffff',
                        display: 'flex',
                        justifyContent: 'center',
                      }}
                    >
                      {currentPreviewUrl ? (
                        <Box
                          sx={{
                            width: previewDevice === 'desktop' ? '100%' : previewWidth,
                            height: '100%',
                            transition: 'width 0.3s ease',
                          }}
                        >
                          <iframe
                            key={`${selectedPageId}-${iframeKey}`}
                            src={currentPreviewUrl}
                            style={{ width: '100%', height: '100%', border: 'none' }}
                            title={`Preview: ${currentPage?.title || 'Page'}`}
                          />
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
                          <Typography level="body-md" sx={{ color: 'text.tertiary' }}>
                            Preview not available. Save and refresh to generate preview.
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </TabPanel>

                  {/* Edits Tab — AI page updates in plain English */}
                  <TabPanel value={1} sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                    <Stack spacing={2} sx={{ maxWidth: 700 }}>
                      <Box>
                        <Typography level="title-md">
                          Edit &ldquo;{currentPage?.title || 'this page'}&rdquo; with AI
                        </Typography>
                        <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                          Describe the change in plain English — edits to this page&apos;s
                          content, the shared header/footer, or your phone number all work
                          from here. Examples: &ldquo;shorten the intro to two
                          sentences&rdquo;, &ldquo;change the phone number to (555)
                          123-4567&rdquo;, &ldquo;add our new tagline to the footer&rdquo;.
                        </Typography>
                      </Box>
                      <Textarea
                        minRows={3}
                        placeholder="What would you like to change on this page?"
                        value={editFeedback}
                        onChange={(e) => setEditFeedback(e.target.value)}
                        disabled={isRevisingPage}
                      />
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Button
                          startDecorator={isRevisingPage ? <CircularProgress size="sm" /> : <EditsIcon />}
                          onClick={() => revisePageMutation.mutate(false)}
                          disabled={editFeedback.trim().length < 5 || isRevisingPage}
                          loading={revisePageMutation.isPending}
                        >
                          {isRevisingPage ? 'Applying your edit...' : 'Apply Edit'}
                        </Button>
                        {isRevisingPage && (
                          <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                            Usually takes 15-45 seconds — the preview refreshes automatically.
                          </Typography>
                        )}
                      </Box>
                      {siteWidePublishHint && (
                        <Alert
                          color="success"
                          variant="soft"
                          sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 1 }}
                          endDecorator={
                            <Button
                              color="success"
                              variant="solid"
                              size="sm"
                              onClick={() => { setSiteWidePublishHint(false); setConfirmDeployOpen(true); }}
                            >
                              Publish Site
                            </Button>
                          }
                        >
                          <Typography level="body-sm">
                            That edit changed <strong>every page</strong>. Review the preview,
                            then publish the site to make it live.
                          </Typography>
                        </Alert>
                      )}
                      {project.status === 'deployed' && (
                        <Alert color="neutral" variant="soft">
                          <Typography level="body-sm">
                            Edits update your draft. Page edits go live with
                            <strong> Publish This Page</strong> (top right); edits that touch
                            every page (header, footer, phone number) go live with
                            <strong> Deploy</strong>.
                          </Typography>
                        </Alert>
                      )}
                      <Alert color="warning" variant="soft">
                        <Typography level="body-xs">
                          AI edits rebuild the page from its structured content — manual
                          changes made in the HTML tab may be overwritten by a later AI edit.
                        </Typography>
                      </Alert>
                      {pageRevisions.length > 0 && (
                        <Box>
                          <Typography level="title-sm" sx={{ mb: 1 }}>Recent edits to this page</Typography>
                          <List size="sm" sx={{ '--ListItem-paddingY': '4px' }}>
                            {pageRevisions.slice(0, 8).map((rev) => (
                              <ListItem key={rev.id}>
                                <Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                    <Typography level="body-sm">&ldquo;{rev.feedback}&rdquo;</Typography>
                                    {rev.status && rev.status !== 'succeeded' && (
                                      <Chip
                                        size="sm"
                                        variant="soft"
                                        color={REVISION_STATUS_COLORS[rev.status] || 'neutral'}
                                        title={rev.status === 'failed' ? rev.error : undefined}
                                      >
                                        {REVISION_STATUS_LABELS[rev.status] || rev.status}
                                      </Chip>
                                    )}
                                    {rev.scope && rev.scope !== 'page_content' && (
                                      <Chip size="sm" variant="outlined" color="primary">
                                        {REVISION_SCOPE_LABELS[rev.scope] || rev.scope}
                                      </Chip>
                                    )}
                                  </Box>
                                  <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                                    {new Date(rev.created_at).toLocaleString()}
                                    {rev.status === 'failed' && rev.error ? ` — ${rev.error}` : ''}
                                    {rev.status === 'succeeded' && rev.result_summary ? ` — ${rev.result_summary}` : ''}
                                  </Typography>
                                </Box>
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      )}
                    </Stack>
                  </TabPanel>

                  {/* Components Tab */}
                  <TabPanel value={2} sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                    <Stack spacing={3} sx={{ maxWidth: 700 }}>
                      {/* Navigation Section */}
                      {pageDetail && pageDetail.page_type !== 'homepage' && !LEGAL_PAGE_TYPES.includes(pageDetail.page_type) && (
                        <Box>
                          <Typography level="title-md">Navigation</Typography>
                          <Typography level="body-sm" sx={{ mb: 1 }}>
                            Where this page is linked from. Changes apply to every page; publish the site to push them live.
                          </Typography>
                          <Stack spacing={1}>
                            <Switch
                              checked={!!pageDetail.in_header_nav}
                              disabled={navToggleMutation.isPending}
                              onChange={(e) => navToggleMutation.mutate({ show_in_header: e.target.checked })}
                              endDecorator="Show in the header menu"
                              sx={{ alignSelf: 'flex-start' }}
                            />
                            <Switch
                              checked={!!pageDetail.in_footer_nav}
                              disabled={navToggleMutation.isPending}
                              onChange={(e) => navToggleMutation.mutate({ show_in_footer: e.target.checked })}
                              endDecorator="Show in the footer links"
                              sx={{ alignSelf: 'flex-start' }}
                            />
                          </Stack>
                        </Box>
                      )}

                      {/* Form Section */}
                      <Box>
                        <Typography level="title-md">Contact Form</Typography>
                        <Typography level="body-sm" sx={{ mb: 1 }}>Select which form to embed on this page</Typography>
                        <Select
                          value={componentConfig?.form_embed_id || 'default'}
                          onChange={(_: any, val: string | null) => updateComponentConfig('form_embed_id', val)}
                        >
                          <Option value="default">Default (first active form)</Option>
                          <Option value="none">None (remove form)</Option>
                          {componentOptions?.forms?.map((f: any) => (
                            <Option key={f.embed_id} value={f.embed_id}>{f.name}</Option>
                          ))}
                        </Select>
                      </Box>

                      {/* Review Widget Section */}
                      <Box>
                        <Typography level="title-md">Review Widget</Typography>
                        <Select
                          value={componentConfig?.review_widget_id || 'default'}
                          onChange={(_: any, val: string | null) => updateComponentConfig('review_widget_id', val)}
                        >
                          <Option value="default">Default (first active widget)</Option>
                          <Option value="none">None (remove reviews)</Option>
                          {componentOptions?.review_widgets?.map((w: any) => (
                            <Option key={w.uuid_public} value={w.uuid_public}>{w.name}</Option>
                          ))}
                        </Select>
                      </Box>

                      {/* Images Section */}
                      {imageSlots.length > 0 && (
                        <Box>
                          <Typography level="title-md" sx={{ mb: 2 }}>Images</Typography>
                          {imageSlots.includes('gallery') && <FormControl sx={{ mb: 2 }}><FormLabel>Gallery presentation</FormLabel>
                            <Select value={componentConfig.gallery_layout || 'grid'} onChange={(_, value) => updateComponentConfig('gallery_layout', value)}>
                              <Option value="grid">Photo grid</Option><Option value="before_after">Matched before / after pairs</Option>
                            </Select><Typography level="body-xs">Pairs use photos tagged before and after with the same project:name tag. Without a matching pair, the photo grid is used.</Typography>
                          </FormControl>}
                          {imageSlots.map(slot => (
                            <Box key={slot} sx={{ mb: 3 }}>
                              <Typography level="title-sm" sx={{ mb: 0.5 }}>
                                {formatSlotLabel(slot)}
                              </Typography>
                              <Typography level="body-xs" sx={{ color: 'text.tertiary', mb: 1 }}>
                                {componentConfig?.images?.[slot]?.url
                                  ? 'Pinned — click the highlighted photo to unpin, or pick another.'
                                  : 'Chosen automatically for this page — click a photo to pin one.'}
                              </Typography>
                              {componentConfig?.images?.[slot]?.url && (
                                <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                                  {['focal_x', 'focal_y', 'alt_text'].map(field => (
                                    <FormControl key={field}>
                                      <FormLabel>{field === 'alt_text' ? 'Photo description' : field === 'focal_x' ? 'Horizontal focus (%)' : 'Vertical focus (%)'}</FormLabel>
                                      <Input type={field === 'alt_text' ? 'text' : 'number'} value={componentConfig.images[slot][field] ?? (field === 'alt_text' ? '' : 50)}
                                        slotProps={field === 'alt_text' ? undefined : { input: { min: 0, max: 100 } }}
                                        onChange={e => { updateComponentConfig('images', { ...componentConfig.images, [slot]: { ...componentConfig.images[slot], [field]: field === 'alt_text' ? e.target.value : Number(e.target.value) } }); }} />
                                    </FormControl>
                                  ))}
                                </Box>
                              )}
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {componentOptions?.photos?.map((photo: any) => {
                                  const selected = isSelectedForSlot(slot, photo);
                                  return (
                                    <Box
                                      key={photo.url}
                                      onClick={() =>
                                        selected ? clearImageSlot(slot) : updateImageSlot(slot, photo)
                                      }
                                      aria-pressed={selected}
                                      sx={{
                                        position: 'relative',
                                        width: 84,
                                        height: 84,
                                        borderRadius: 'sm',
                                        overflow: 'hidden',
                                        cursor: 'pointer',
                                        border: '3px solid',
                                        borderColor: selected ? 'primary.500' : 'neutral.outlinedBorder',
                                        boxShadow: selected
                                          ? '0 0 0 3px var(--joy-palette-primary-200)'
                                          : 'none',
                                        '&:hover': { borderColor: selected ? 'primary.600' : 'primary.300' },
                                        transition: 'border-color 0.12s, box-shadow 0.12s',
                                      }}
                                    >
                                      <img
                                        src={photo.thumbnail_url || photo.url}
                                        alt={photo.name}
                                        loading="lazy"
                                        style={{
                                          width: '100%',
                                          height: '100%',
                                          objectFit: 'cover',
                                          opacity: selected ? 1 : 0.92,
                                        }}
                                      />
                                      {selected && (
                                        <Box
                                          sx={{
                                            position: 'absolute',
                                            top: 2,
                                            right: 2,
                                            bgcolor: 'primary.500',
                                            borderRadius: '50%',
                                            width: 22,
                                            height: 22,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            boxShadow: 'sm',
                                          }}
                                        >
                                          <SelectedIcon size={16} weight="fill" color="#fff" />
                                        </Box>
                                      )}
                                    </Box>
                                  );
                                })}
                              </Box>
                              <Button
                                size="sm"
                                variant="plain"
                                color="neutral"
                                onClick={() => clearImageSlot(slot)}
                                sx={{ mt: 0.5 }}
                              >
                                Reset to Default
                              </Button>
                            </Box>
                          ))}
                        </Box>
                      )}

                      {imageSlots.length === 0 && !componentOptions?.photos?.length && (
                        <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                          No image slots detected in this page&apos;s HTML. Add {`<!-- APEXOS:IMAGE:name -->`} markers to enable image swapping.
                        </Typography>
                      )}
                    </Stack>
                  </TabPanel>

                  {/* SEO Tab */}
                  <TabPanel value={3} sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                    <Stack spacing={3} sx={{ maxWidth: 700 }}>
                      {projectId && selectedPageId && pageDetail && <SeoAuditPanel key={selectedPageId} projectId={projectId} pageId={selectedPageId} slug={pageDetail.slug} />}
                      <FormControl>
                        <FormLabel>
                          Meta Title
                          <Typography
                            level="body-xs"
                            component="span"
                            sx={{
                              ml: 1,
                              color: metaTitle.length > 60 ? 'danger.500' : 'text.tertiary',
                            }}
                          >
                            {metaTitle.length}/60
                          </Typography>
                        </FormLabel>
                        <Input
                          value={metaTitle}
                          onChange={(e) => setMetaTitle(e.target.value)}
                          placeholder="Page title for search engines"
                          slotProps={{ input: { maxLength: 60 } }}
                        />
                      </FormControl>

                      <FormControl>
                        <FormLabel>
                          Meta Description
                          <Typography
                            level="body-xs"
                            component="span"
                            sx={{
                              ml: 1,
                              color: metaDescription.length > 160 ? 'danger.500' : 'text.tertiary',
                            }}
                          >
                            {metaDescription.length}/160
                          </Typography>
                        </FormLabel>
                        <Textarea
                          value={metaDescription}
                          onChange={(e) => setMetaDescription(e.target.value)}
                          placeholder="Brief description for search engine results"
                          minRows={3}
                          slotProps={{ textarea: { maxLength: 160 } }}
                        />
                      </FormControl>

                      {/* FAQ structured data (FAQPage JSON-LD) */}
                      <Box>
                        <Typography level="title-md">Page FAQs</Typography>
                        <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 1 }}>
                          Generate 10–15 questions and answers specific to this page as
                          visible FAQs with matching structured data. Review the answers before publishing.
                        </Typography>
                        {(() => {
                          const parsed = typeof pageDetail?.json_ld === 'object' && pageDetail?.json_ld
                            ? pageDetail.json_ld
                            : null;
                          const entities = parsed
                            ? [parsed, ...(Array.isArray((parsed as Record<string, unknown>)['@graph'])
                                ? ((parsed as Record<string, unknown>)['@graph'] as Array<Record<string, unknown>>)
                                : [])]
                            : [];
                          const faqEntity = entities.find((e) => e && e['@type'] === 'FAQPage');
                          const faqCount = faqEntity && Array.isArray(faqEntity.mainEntity)
                            ? faqEntity.mainEntity.length
                            : 0;
                          return faqCount > 0 ? (
                            <Alert color="success" variant="soft" sx={{ mb: 1 }}>
                              <Typography level="body-sm">
                                This page has FAQ schema with <strong>{faqCount}</strong> questions.
                                Regenerating replaces them.
                              </Typography>
                            </Alert>
                          ) : null;
                        })()}
                        <Button
                          variant="outlined"
                          color="primary"
                          startDecorator={isGeneratingFaqs ? <CircularProgress size="sm" /> : <SeoIcon />}
                          onClick={() => generateFaqsMutation.mutate()}
                          loading={generateFaqsMutation.isPending}
                          disabled={isGeneratingFaqs}
                        >
                          {isGeneratingFaqs ? 'Generating FAQs…' : 'Generate Page FAQs'}
                        </Button>
                        {isGeneratingFaqs && (
                          <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 0.5 }}>
                            Usually takes 15–30 seconds — the structured data below updates automatically.
                          </Typography>
                        )}
                      </Box>

                      <FormControl>
                        <FormLabel>Structured Data (JSON-LD)</FormLabel>
                        <CodeEditor
                          value={jsonLd}
                          onChange={setJsonLd}
                          language="json"
                          height="200px"
                        />
                      </FormControl>
                    </Stack>
                  </TabPanel>

                  {/* HTML Tab */}
                  <TabPanel value={4} sx={{ flex: 1, overflow: 'hidden', p: 2 }}>
                    <CodeEditor
                      value={pageHtml}
                      onChange={setPageHtml}
                      language="html"
                      height="calc(100vh - 240px)"
                    />
                  </TabPanel>

                  {/* CSS Tab */}
                  <TabPanel value={5} sx={{ flex: 1, overflow: 'hidden', p: 2 }}>
                    <CodeEditor
                      value={pageCss}
                      onChange={setPageCss}
                      language="css"
                      height="calc(100vh - 240px)"
                    />
                  </TabPanel>
                </Tabs>
                </>
              )}
            </Box>
          ) : (
            // No page selected
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                flex: 1,
                color: 'text.tertiary',
              }}
            >
              <LanguageIcon size={48} style={{ marginBottom: 16, opacity: 0.5 }} />
              <Typography level="body-lg" sx={{ px: 2, textAlign: 'center' }}>Select a page to begin editing</Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Deploy confirmation */}
      <AddPageDialog
        open={addPageOpen}
        projectId={projectId!}
        pages={project?.pages || []}
        onClose={() => setAddPageOpen(false)}
        onCreated={handlePageCreated}
      />
      <Modal open={confirmDeployOpen} onClose={() => setConfirmDeployOpen(false)}>
        <ModalDialog variant="outlined" role="alertdialog">
          <DialogTitle>Deploy to production?</DialogTitle>
          <DialogContent>
            This publishes all {project.pages_count} pages to your live website
            {project.production_url ? ` at ${project.production_url}` : ''}. Visitors will see the
            changes as soon as the deployment finishes.
            {!project.production_url && (
              <> First-time publish: a brand-new site can take up to an hour to come
              online everywhere while hosting and CDN records propagate.</>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              color="success"
              startDecorator={<RocketIcon />}
              onClick={() => deployProject.mutate()}
              loading={deployProject.isPending}
            >
              Deploy
            </Button>
            <Button variant="plain" color="neutral" onClick={() => setConfirmDeployOpen(false)}>
              Cancel
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      {/* Republish chrome confirmation */}
      <Modal open={confirmRepublishOpen} onClose={() => setConfirmRepublishOpen(false)}>
        <ModalDialog variant="outlined" role="alertdialog">
          <DialogTitle>Publish header/footer changes to all {project.pages_count} pages?</DialogTitle>
          <DialogContent>
            This propagates your edited header, hero, and footer to every page. It is free — no AI
            generation is involved.
            {project.status === 'deployed' && ' Your live site will be redeployed with the changes.'}
          </DialogContent>
          <DialogActions>
            <Button
              startDecorator={<RefreshIcon />}
              onClick={() => republishChrome.mutate()}
              loading={republishChrome.isPending}
            >
              Publish to All Pages
            </Button>
            <Button variant="plain" color="neutral" onClick={() => setConfirmRepublishOpen(false)}>
              Not Now
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>
    </Box>
  );
};

export default WebsiteManager;
